import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getPendingUsers, getUsers, approveUser, rejectUser, suspendUser, createClientAccount, updateUser, deleteUser, createUser } from '@/utils/api';
import { toast } from 'sonner';
import { Check, X, Ban, Users, UserCheck, Clock, AlertCircle, UserPlus, Search, Key, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import CollapsibleLayout from '@/components/CollapsibleLayout';
import { USER_ROLE_LABELS } from '@/utils/constants';
import { ClientSelect } from '@/lib/ClientSelect';

const UserManagementPage = () => {
  const { user } = useAuth();
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');

  const [editingUser, setEditingUser] = useState(null);
  const [editPassword, setEditPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSavePassword = async (userId) => {
    if (!editPassword.trim()) { toast.error('Enter a password'); return; }
    setSaving(true);
    try {
      await updateUser(userId, { password: editPassword });
      toast.success('Password updated');
      setEditingUser(null);
      setEditPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (userId, name) => {
    if (!confirm(`Delete user "${name}"? This cannot be undone.`)) return;
    try {
      await deleteUser(userId);
      toast.success(`User "${name}" deleted`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  // Create Client Account form
  const [clientForm, setClientForm] = useState({ email: '', name: '', password: '', client_code: '' });
  const [creatingClient, setCreatingClient] = useState(false);

  // Create User form
  const [newUserForm, setNewUserForm] = useState({ email: '', name: '', password: '', role: 'executor', company_name: '', client_code: '' });
  const [creatingUser, setCreatingUser] = useState(false);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUserForm.email || !newUserForm.name || !newUserForm.password || !newUserForm.role) {
      toast.error('Name, Email, Password and Role are required');
      return;
    }
    if (newUserForm.role === 'customer' && !newUserForm.client_code) {
      toast.error('Client Code is required for Customer accounts');
      return;
    }
    setCreatingUser(true);
    try {
      await createUser(newUserForm);
      toast.success(`User account created for ${newUserForm.name} (${newUserForm.role})`);
      setNewUserForm({ email: '', name: '', password: '', role: 'executor', company_name: '', client_code: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user account');
    } finally {
      setCreatingUser(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [pendingRes, allRes] = await Promise.all([
        getPendingUsers(),
        getUsers()
      ]);
      setPendingUsers(pendingRes.data);
      setAllUsers(allRes.data);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    try {
      await approveUser(userId);
      toast.success('User approved successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve user');
    }
  };

  const handleReject = async (userId) => {
    if (!confirm('Are you sure you want to reject this user?')) return;
    try {
      await rejectUser(userId);
      toast.success('User rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject user');
    }
  };

  const handleSuspend = async (userId) => {
    if (!confirm('Are you sure you want to suspend this user?')) return;
    try {
      await suspendUser(userId);
      toast.success('User suspended');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to suspend user');
    }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    if (!clientForm.email || !clientForm.name || !clientForm.password || !clientForm.client_code) {
      toast.error('All fields are required');
      return;
    }
    setCreatingClient(true);
    try {
      await createClientAccount(clientForm);
      toast.success(`Client account created for ${clientForm.name}`);
      setClientForm({ email: '', name: '', password: '', client_code: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create client account');
    } finally {
      setCreatingClient(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      approved: 'bg-green-100 text-green-700 border-green-200',
      pending_approval: 'bg-amber-100 text-amber-700 border-amber-200',
      rejected: 'bg-red-100 text-red-700 border-red-200',
      suspended: 'bg-gray-100 text-gray-700 border-gray-200',
    };
    const labels = {
      approved: 'Active',
      pending_approval: 'Pending',
      rejected: 'Rejected',
      suspended: 'Suspended',
    };
    return (
      <span className={`px-2 py-0.5 text-xs rounded border ${styles[status] || styles.pending_approval}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getRoleBadge = (role) => {
    const styles = {
      master_admin: 'bg-purple-100 text-purple-700',
      admin: 'bg-blue-100 text-blue-700',
      special_executor: 'bg-orange-100 text-orange-700 border border-orange-200',
      final_executor: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
      cad_head: 'bg-teal-100 text-teal-700',
      design_head: 'bg-cyan-100 text-cyan-700',
      designer: 'bg-indigo-100 text-indigo-700',
      customer: 'bg-green-100 text-green-700',
    };
    return (
      <span className={`px-2 py-0.5 text-xs rounded ${styles[role] || 'bg-gray-100 text-gray-700'}`}>
        {USER_ROLE_LABELS[role] || role}
      </span>
    );
  };

  return (
    <CollapsibleLayout>
      <div className="h-screen bg-background overflow-auto">
        <div className="max-w-6xl mx-auto p-6">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-foreground">User Management</h1>
            <p className="text-muted-foreground text-sm mt-1">Approve new registrations and manage user access</p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-lg border border-border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-100">
                  <Clock size={18} className="text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{pendingUsers.length}</p>
                  <p className="text-xs text-muted-foreground">Pending Approval</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg border border-border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-100">
                  <UserCheck size={18} className="text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">
                    {allUsers.filter(u => u.user_status === 'approved').length}
                  </p>
                  <p className="text-xs text-muted-foreground">Active Users</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg border border-border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100">
                  <Users size={18} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{allUsers.length}</p>
                  <p className="text-xs text-muted-foreground">Total Users</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg border border-border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-100">
                  <UserPlus size={18} className="text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">
                    {allUsers.filter(u => u.role === 'customer').length}
                  </p>
                  <p className="text-xs text-muted-foreground">Client Accounts</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg border border-border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-100">
                  <AlertCircle size={18} className="text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">
                    {allUsers.filter(u => u.user_status === 'suspended').length}
                  </p>
                  <p className="text-xs text-muted-foreground">Suspended</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-lg border border-border">
            <div className="border-b border-border">
              <div className="flex">
                <button
                  onClick={() => setActiveTab('pending')}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'pending'
                      ? 'border-amber-500 text-amber-600'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Pending Approval ({pendingUsers.length})
                </button>
                <button
                  onClick={() => setActiveTab('all')}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'all'
                      ? 'border-amber-500 text-amber-600'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  All Users ({allUsers.length})
                </button>
                <button
                  onClick={() => setActiveTab('create-user')}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'create-user'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid="create-user-tab"
                >
                  <span className="flex items-center gap-1"><UserPlus size={14} /> Create User</span>
                </button>
                <button
                  onClick={() => setActiveTab('create-client')}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'create-client'
                      ? 'border-emerald-500 text-emerald-600'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid="create-client-tab"
                >
                  <span className="flex items-center gap-1"><UserPlus size={14} /> Create Client Account</span>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-4">
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading...</div>
              ) : activeTab === 'pending' ? (
                pendingUsers.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <UserCheck size={40} className="mx-auto mb-2 text-green-500" />
                    <p>No pending registrations</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pendingUsers.map((u) => (
                      <div key={u.id} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                        <div className="flex items-center gap-4">
                          <div 
                            className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium"
                            style={{ backgroundColor: 'hsl(45, 90%, 50%)', color: 'hsl(220, 60%, 20%)' }}
                          >
                            {u.name?.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium text-foreground">{u.name}</p>
                            <p className="text-sm text-muted-foreground">{u.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {getRoleBadge(u.role)}
                          {u.company_name && (
                            <span className="text-xs text-muted-foreground">{u.company_name}</span>
                          )}
                          <Button
                            size="sm"
                            onClick={() => handleApprove(u.id)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Check size={14} className="mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleReject(u.id)}
                          >
                            <X size={14} className="mr-1" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              ) : activeTab === 'create-client' ? (
                <div className="max-w-lg mx-auto py-4">
                  <div className="mb-4">
                    <h3 className="text-base font-semibold text-foreground">Create Client Login</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">Create a login for a client mapped to their Client Code. They will only see tasks with that code.</p>
                  </div>
                  <form onSubmit={handleCreateClient} className="space-y-4">
                    <div>
                      <Label className="text-xs font-medium">Client Code</Label>
                      <div className="mt-1">
                        <ClientSelect
                          value={clientForm.client_code}
                          onChange={(val) => setClientForm(prev => ({ ...prev, client_code: val }))}
                          placeholder="Select client from master list..."
                        />
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-1">This client will see all tasks with this code</p>
                    </div>
                    <div>
                      <Label className="text-xs font-medium">Contact Name</Label>
                      <Input
                        value={clientForm.name}
                        onChange={e => setClientForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="e.g. John Smith"
                        className="mt-1 h-9"
                        data-testid="client-name-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-medium">Email</Label>
                      <Input
                        type="email"
                        value={clientForm.email}
                        onChange={e => setClientForm(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="client@example.com"
                        className="mt-1 h-9"
                        data-testid="client-email-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-medium">Password</Label>
                      <Input
                        type="text"
                        value={clientForm.password}
                        onChange={e => setClientForm(prev => ({ ...prev, password: e.target.value }))}
                        placeholder="Set a password for the client"
                        className="mt-1 h-9"
                        data-testid="client-password-input"
                      />
                    </div>
                    <Button type="submit" disabled={creatingClient} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="create-client-submit-btn">
                      <UserPlus size={14} className="mr-2" />
                      {creatingClient ? 'Creating...' : 'Create Client Account'}
                    </Button>
                  </form>

                  {/* Existing client accounts list */}
                  {allUsers.filter(u => u.role === 'customer').length > 0 && (
                    <div className="mt-8">
                      <h4 className="text-sm font-semibold text-foreground mb-3">Existing Client Accounts</h4>
                      <div className="space-y-2">
                        {allUsers.filter(u => u.role === 'customer').map(u => (
                          <div key={u.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-border/50">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-xs font-bold text-emerald-700">
                                {u.name?.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <p className="text-sm font-medium">{u.name}</p>
                                <p className="text-[11px] text-muted-foreground">{u.email}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {u.client_code && (
                                <span className="px-2 py-0.5 text-[10px] font-medium rounded bg-emerald-100 text-emerald-700 border border-emerald-200" data-testid={`client-code-badge-${u.client_code}`}>
                                  {u.client_code}
                                </span>
                              )}
                              {getStatusBadge(u.user_status)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : activeTab === 'create-user' ? (
                <div className="max-w-lg mx-auto py-4">
                  <div className="mb-4">
                    <h3 className="text-base font-semibold text-foreground">Create User Account</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">Create a new user account with a specific role.</p>
                  </div>
                  <form onSubmit={handleCreateUser} className="space-y-4">
                    <div>
                      <Label className="text-xs font-medium">Name</Label>
                      <Input
                        value={newUserForm.name}
                        onChange={e => setNewUserForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="e.g. John Doe"
                        className="mt-1 h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-medium">Email</Label>
                      <Input
                        type="email"
                        value={newUserForm.email}
                        onChange={e => setNewUserForm(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="user@example.com"
                        className="mt-1 h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-medium">Password</Label>
                      <Input
                        type="text"
                        value={newUserForm.password}
                        onChange={e => setNewUserForm(prev => ({ ...prev, password: e.target.value }))}
                        placeholder="Set a password"
                        className="mt-1 h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-medium">Role</Label>
                      <select
                        value={newUserForm.role}
                        onChange={e => setNewUserForm(prev => ({ ...prev, role: e.target.value, client_code: '', company_name: '' }))}
                        className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 mt-1"
                      >
                        <option value="admin">Admin</option>
                        <option value="special_executor">Special Executor</option>
                        <option value="final_executor">Final Executor</option>
                        <option value="executor">Executor</option>
                        <option value="customer">Customer</option>
                        <option value="stone_supplier">Stone Supplier</option>
                        <option value="stone_admin">Stone Admin</option>
                      </select>
                    </div>

                    {newUserForm.role === 'customer' && (
                      <div>
                        <Label className="text-xs font-medium">Client Code</Label>
                        <div className="mt-1">
                          <ClientSelect
                            value={newUserForm.client_code}
                            onChange={(val) => setNewUserForm(prev => ({ ...prev, client_code: val }))}
                            placeholder="Select client from master list..."
                          />
                        </div>
                      </div>
                    )}

                    {(newUserForm.role === 'customer' || newUserForm.role === 'stone_supplier') && (
                      <div>
                        <Label className="text-xs font-medium">Company Name</Label>
                        <Input
                          value={newUserForm.company_name}
                          onChange={e => setNewUserForm(prev => ({ ...prev, company_name: e.target.value }))}
                          placeholder="e.g. Acme Gems"
                          className="mt-1 h-9"
                        />
                      </div>
                    )}

                    <Button type="submit" disabled={creatingUser} className="w-full bg-blue-600 hover:bg-blue-700">
                      <UserPlus size={14} className="mr-2" />
                      {creatingUser ? 'Creating...' : 'Create User Account'}
                    </Button>
                  </form>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-xs text-muted-foreground border-b border-border">
                        <th className="pb-3 font-medium">User</th>
                        <th className="pb-3 font-medium">Email</th>
                        <th className="pb-3 font-medium">Role</th>
                        <th className="pb-3 font-medium">Client Code</th>
                        <th className="pb-3 font-medium">Company</th>
                        <th className="pb-3 font-medium">Status</th>
                        <th className="pb-3 font-medium text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="text-sm">
                      {allUsers.map((u) => (
                        <tr key={u.id} className="border-b border-border/50 last:border-0">
                          <td className="py-3">
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium"
                                style={{ backgroundColor: 'hsl(45, 90%, 50%)', color: 'hsl(220, 60%, 20%)' }}
                              >
                                {u.name?.charAt(0).toUpperCase()}
                              </div>
                              <span className="font-medium">{u.name}</span>
                            </div>
                          </td>
                          <td className="py-3 text-muted-foreground">{u.email}</td>
                          <td className="py-3">{getRoleBadge(u.role)}</td>
                          <td className="py-3">
                            {u.client_code ? (
                              <span className="px-2 py-0.5 text-[10px] font-medium rounded bg-emerald-100 text-emerald-700 border border-emerald-200">{u.client_code}</span>
                            ) : '-'}
                          </td>
                          <td className="py-3 text-muted-foreground">{u.company_name || '-'}</td>
                          <td className="py-3">{getStatusBadge(u.user_status)}</td>
                          <td className="py-3 text-right">
                            <div className="flex gap-1 justify-end items-center">
                              {/* Password change */}
                              {editingUser === u.id ? (
                                <div className="flex items-center gap-1">
                                  <Input type="text" placeholder="New password" value={editPassword} onChange={e => setEditPassword(e.target.value)}
                                    className="h-7 w-28 text-xs" data-testid={`password-input-${u.id}`} />
                                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-green-600" onClick={() => handleSavePassword(u.id)} disabled={saving} data-testid={`save-password-${u.id}`}><Check size={14} /></Button>
                                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditingUser(null); setEditPassword(''); }}><X size={14} /></Button>
                                </div>
                              ) : (
                                <Button size="sm" variant="ghost" className="h-7 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                  onClick={() => { setEditingUser(u.id); setEditPassword(''); }} data-testid={`change-password-${u.id}`}>
                                  <Key size={12} className="mr-1" />Password
                                </Button>
                              )}
                              {/* Suspend */}
                              {u.user_status === 'approved' && u.id !== user?.id && (
                                <Button size="sm" variant="ghost" className="h-7 text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                  onClick={() => handleSuspend(u.id)}><Ban size={12} className="mr-1" />Suspend</Button>
                              )}
                              {/* Approve pending */}
                              {u.user_status === 'pending_approval' && (
                                <>
                                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-green-600" onClick={() => handleApprove(u.id)}><Check size={14} /></Button>
                                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600" onClick={() => handleReject(u.id)}><X size={14} /></Button>
                                </>
                              )}
                              {/* Delete */}
                              {u.id !== user?.id && (
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                                  onClick={() => handleDeleteUser(u.id, u.name)} data-testid={`delete-user-${u.id}`}><Trash2 size={12} /></Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </CollapsibleLayout>
  );
};

export default UserManagementPage;
